import React, { useState, useEffect, useRef } from 'react';
import { Box, Tabs, Tab, Typography, CircularProgress, Paper, Fade, Collapse, TablePagination, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useInventoryData } from '../Component/BondLadderComponents/useBondLadderHook.js';
import { useBondLadderRungFlowStore } from '../Store/bondLadderRungFlowStore';
import BondLadderHeader from '../Component/BondLadderComponents/BondLadderHeader.jsx';
import { useAuth } from '../Context/AuthProvider';
import { useLocation, useNavigate } from 'react-router-dom';
import CustomizeColumns from "../Component/CustomizeColumns";
import OrderEntry from "../Component/OrderEntry.jsx";
import BondDetailsModal from "../Component/BondDetails/BondDetailsModal";
import { useActualOrderStore } from "../Store/actualOrderStore.js";
import BondTable from '../Component/BondLadderComponents/BondTable.jsx';
import { useSelectedLadderOrdersStore } from '../Store/selectedLadderOrdersStore';
import SelectedOrdersPreviewTable from '../Component/BondLadderComponents/SelectedOrdersPreviewTable.jsx';
import { clearConfigIfOutdated, updateColumnConfigIfNeeded } from "../Utils/columnVersionManager";
import { getPreference, setPreference } from "../Utils/userPreferenceManger";
import ResizableContainer from '../Component/BondLadderComponents/ResizableContainer.jsx';
import { downloadCSV } from '../Component/BondLadderComponents/utils.js';
import { tokens, withAlpha } from '../Common/designTokens'; // added

const VerticalTabs = styled(Tabs)({
  borderRight: `1px solid ${tokens.colors.bodyBorderColor50}`,
  '& .MuiTabs-indicator': {
    left: 0,
    right: 'auto'
  },
  minWidth: '120px',
  width: 'auto',
  backgroundColor: tokens.colors.defaultGridRowColor,
  '& .MuiTabs-scroller': {
    overflowY: 'auto !important',
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      background: withAlpha('#000000', 0.06),
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: tokens.colors.scrollbarThumb,
      borderRadius: '4px',
      '&:hover': {
        backgroundColor: withAlpha(tokens.colors.fontColour, 0.12),
      },
    },
  },
  // Add flexbox styling to allow tabs to fill container equally
  '& .MuiTabs-flexContainer': {
    height: '100%',
    '& .MuiButtonBase-root:not(:last-child)': {
      borderBottom: `1px solid ${tokens.colors.bodyBorderColor50}`
    }
  }
});

const VerticalTab = styled(Tab)(({ theme, selected, hasOrders }) => ({
  alignItems: 'flex-start',
  textAlign: 'left',
  padding: '8px 12px',
  color: tokens.colors.fontColour,
  minHeight: '40px',
  width: '100%',
  position: 'relative', // For positioning the dot
  borderBottom: `1px solid ${tokens.colors.bodyBorderColor50}`, // Add subtle separator line
  '&.Mui-selected': {
    color: tokens.colors.white, // show white text when selected
    backgroundColor: tokens.palette.primary,
    borderRadius: 0
  },
  '&:hover': {
    backgroundColor: selected ? withAlpha(tokens.palette.primary, 0.9) : withAlpha(tokens.colors.fontColour, 0.04)
  },
  '&::after': hasOrders ? {
    content: '""',
    position: 'absolute',
    top: '50%',
    right: '12px',
    width: '6px',
    height: '6px',
    backgroundColor: selected ? tokens.colors.white : tokens.colors.fontColour, // dot contrasts with selection
    borderRadius: '50%',
    transform: 'translateY(-50%)',
  } : {}
}));

const BondLadderDetails = () => {
  const allColumns = [
    { key: "action", name: "Side" },
    { key: "brokerDealer", name: "Broker" },
    { key: "state", name: "State" },
    { key: "ratingMoody", name: "Moody's" },
    { key: "ratingSnp", name: "S&P" },
    { key: "cusip", name: "CUSIP" },
    { key: "issuer", name: "Issuer" }, // Added issuer column
    { key: "bondType", name: "Type" },
    { key: "bondSubType", name: "Sub Type" },
    { key: "description", name: "Description" },
    { key: "qty", name: "Qty" },
    { key: "minQty", name: "Min Qty" },
    { key: "maxQty", name: "Max Qty" },
    { key: "price", name: "Price" },
    { key: "coupon", name: "Coupon" },
    { key: "maturity", name: "Maturity" },
    { key: "callable", name: "Callable" },
    { key: "taxable", name: "Taxable" },
    { key: "securityCharge", name: "Factored Securities", width: 120 },
    { key: "poolFactor", name: "Factor", width: 95 },
    { key: "ytm", name: "YTM" },
    { key: "ytw1", name: "YTW" },
    { key: "ytc", name: "YTC" },
    { key: "accruedInterest", name: "Accrued Interest" },
    { key: "estimatedTotal", name: "Estimated Total" },
    { key: "duration", name: "Duration" },
    { key: "insured", name: "Insured" },
    { key: "paymentFrequency", name: "Payment Frequency" },
    { key: "sector", name: "Sector" },
    { key: "incomeSource", name: "Income Source" },
    { key: "nextCallDate", name: "Next Call Date" },
    { key: "altMinTax", name: "Alt Min Tax" },
    { key: "conventionMethod", name: "Convention Method" },
    { key: "underlyingIssuerRating", name: "Underlying Issuer Rating" },
  ];
  
  const location = useLocation();
  const navigate = useNavigate();

  const [source, setSource] = useState(location.state?.source || "noSource");
  const [selectedColumns, setSelectedColumns] = useState(() => {
    // Check and update column configuration if needed
    clearConfigIfOutdated('bondLadder', allColumns);
    // Then get the saved columns using role-based preferences
    const saved = getPreference('selectedColumns_bondLadder');
    return saved || [...allColumns];
  });
  const [selectedRung, setSelectedRung] = useState(0);
  const { ladderData, rungsDetail } = useBondLadderRungFlowStore();
  const [currentFilters, setCurrentFilters] = useState(null);
  const [page, setPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  // Add sorting state
  const [sortConfig, setSortConfig] = useState({ key: "ytm", direction: "desc" });//imp
  const [sortConfigForFetchingData, setSortConfigForFetchingData] = useState(() => {
    return sortConfig && sortConfig.key
      ? { columnToSort: sortConfig.key, sortingDirection: sortConfig.direction }
      : null;
  });
  const { token } = useAuth();
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  // Update useInventoryData hook call to include sorting parameters
  const { bondData, totalElements, lastUpdatedAt, loading } = useInventoryData(
    token,
    currentFilters,
    page,
    itemsPerPage,
    source,
    selectedRung,
    refreshTrigger,
    sortConfigForFetchingData // Add sort config for fetching data
  );

  // Handler for sorting
  const handleSortingDataFetching = (columnKey, direction) => {
    setSortConfig({ key: columnKey, direction });
    setSortConfigForFetchingData({
      columnToSort: columnKey,
      sortingDirection: direction
    });
  };
  
  const [ccOpen, setCcOpen] = useState(false);
  const [csvLoading, setCsvLoading] = useState(false);
  const customizeButtonRef = useRef(null);
  const [selectCusip, setSelectCusip] = useState("");
  const [oeOpen, setOeOpen] = useState(false);
  const [readOnly, setReadOnly] = useState(false);
  const [bondDetailsOpen, setBondDetailsOpen] = useState(false);
  const [selectedBondDetails, setSelectedBondDetails] = useState(null);
  const { setActualOrderData: setActualOrderDataZus } = useActualOrderStore();

  const [isRungSwitching, setIsRungSwitching] = useState(false);
  const previousRung = useRef(selectedRung);
  const loadingTimerRef = useRef(null);

  const { selectedOrders, removeOrder: removeOrderFromStore } = useSelectedLadderOrdersStore();
  const hasSelectedOrders = Object.keys(selectedOrders).length > 0;
  const [isPlacingOrders, setIsPlacingOrders] = useState(false);

  const removeOrder = (rungIndex) => {
    removeOrderFromStore(rungIndex);
    setRefreshTrigger(prev => prev + 1); // Trigger a refresh of inventory data
  };

  const handleDownloadCSV = async (format = "xlsx") => {
    setCsvLoading(true);
    try {
      const ladderName = ladderData?.nameOfTheLadder || ladderData?.ladderName || "ladder";
      await downloadCSV(token, currentFilters, totalElements, selectedColumns, format, `ladder_${ladderName}_inventory_data`, {
        sortConfig: sortConfigForFetchingData,
        sectionKey: "bondLadder",
      });
    } catch (e) {
      console.error('Failed to download ladder XLSX:', e);
    } finally {
      setCsvLoading(false);
    }
  };

  const handleDeleteChip = (key) => {
    if (currentFilters) {
      const newFilters = { ...currentFilters };
      delete newFilters[key];
      setCurrentFilters(newFilters);
    }
  };
  // --- Resizable preview setup ---
  const layoutRef = useRef(null);
  const paginationRef = useRef(null);
  const [previewHeight, setPreviewHeight] = useState(230);
  const [allowedMaxPreviewHeight, setAllowedMaxPreviewHeight] = useState(230);
  const [isPreviewResizing, setIsPreviewResizing] = useState(false); // NEW

  // constants aligned with SelectedOrdersPreviewTable
  const PREVIEW_HEADER_BAR_HEIGHT = 40;
  const TABLE_HEADER_HEIGHT = 40;
  const ROW_HEIGHT = 40;
  const ROW_SAFETY_MARGIN = 10; // same as showPreviewOnly = false
  const PADDING = 16;
  const MIN_TABLE_HEIGHT = 100;
  const MIN_MAIN_TOP_HEIGHT = 50; // keep main table area visible

  // Compute row count in preview
  const selectedOrderCount = Object.keys(selectedOrders).length;

  // Compute the natural content height for the table inside preview
  const tableNaturalHeight = selectedOrderCount === 0
    ? 0
    : TABLE_HEADER_HEIGHT + (selectedOrderCount * (ROW_HEIGHT + ROW_SAFETY_MARGIN)) + PADDING;
  // new: compute min height that shows up to 3 rows (1→1,2→2,3→3,4+→3)
  const previewMinHeight = Math.max(
    PREVIEW_HEADER_BAR_HEIGHT + MIN_TABLE_HEIGHT,
    PREVIEW_HEADER_BAR_HEIGHT +
      TABLE_HEADER_HEIGHT +
      (Math.min(selectedOrderCount, 3) * (ROW_HEIGHT + ROW_SAFETY_MARGIN)) +
      PADDING
  );
  // Recalculate allowed max height when layout changes
  useEffect(() => {
    const recalc = () => {
      const layoutH = layoutRef.current ? layoutRef.current.clientHeight : 0;
      const paginationH = paginationRef.current ? paginationRef.current.clientHeight : 0;

      // Maximum that fits on screen while keeping a minimum main area
      const maxByLayout = Math.max(
        0,
        layoutH - (MIN_MAIN_TOP_HEIGHT + paginationH)
      );

      // Maximum that matches content (header + table content; no blank gap)
      const maxByContent = PREVIEW_HEADER_BAR_HEIGHT + Math.max(MIN_TABLE_HEIGHT, tableNaturalHeight);

      const newAllowedMax = Math.max(0, Math.min(maxByLayout, maxByContent));
      setAllowedMaxPreviewHeight(newAllowedMax);

      // Clamp current preview height into the new bounds
      setPreviewHeight((h) => Math.max(PREVIEW_HEADER_BAR_HEIGHT + MIN_TABLE_HEIGHT, Math.min(newAllowedMax || (PREVIEW_HEADER_BAR_HEIGHT + MIN_TABLE_HEIGHT), h)));
    };

    recalc();
    window.addEventListener('resize', recalc);
    return () => window.removeEventListener('resize', recalc);
  }, [tableNaturalHeight]);

  useEffect(() => {
    // Accept state-only updates (e.g., after Edit -> View Inventory simulation)
    if (!location.state) return;
    if (location.state.ladderData) {
      useBondLadderRungFlowStore.getState().setLadderData(location.state.ladderData);
    }
    // Always update the source if provided on navigation
    setSource(location.state.source || "noSource");
  }, [location.state]);

  useEffect(() => {
      //if data is not available, redirect to /order?view=0&tab=2
    if (!ladderData) {
      navigate('/order?view=0&tab=2', { replace: true });
      return;
    }
  }, [ladderData, navigate]);

  const executedOrderForSelectedRung =
    selectedOrders[selectedRung] &&
    (
      selectedOrders[selectedRung].isEditable === false ||
      selectedOrders[selectedRung].status === 'DONE' ||
      selectedOrders[selectedRung].protocol === 'RFQ'
    )
      ? selectedOrders[selectedRung]
      : null;
  const isSelectedRungExecuted = Boolean(executedOrderForSelectedRung);

  // Use an effective loading flag that ignores hook loading when rung is executed
  const uiLoading = (loading || isRungSwitching) && !isSelectedRungExecuted;
  // NEW: Ensure selectedRung stays within bounds when number of rungs changes (e.g., after Edit)
  useEffect(() => {
    if (!Array.isArray(rungsDetail)) return;
    const maxIndex = Math.max(0, rungsDetail.length - 1);
    if (selectedRung > maxIndex) {
      setSelectedRung(maxIndex);
      setPage(0);
    }
  }, [rungsDetail.length, selectedRung]);

  useEffect(() => {
    if (ladderData && rungsDetail[selectedRung]) {
      const newFilters = {
        //add here
        ...(location.state || {}),
        minMaturity: rungsDetail[selectedRung].minMaturity,
        maxMaturity: rungsDetail[selectedRung].maxMaturity,
        type: ladderData.type === 'Treasury' ? 'Gov' :
          ladderData.type === 'Corporates' ? 'Corp' : 'Muni'
      };
      // If this rung already has an executed order, skip calling the filters API
      if (isSelectedRungExecuted) {
        setCurrentFilters(null);
      } else {
        setCurrentFilters(newFilters);
      }
    }
  }, [selectedRung, ladderData, rungsDetail, location.state, isSelectedRungExecuted, selectedOrders]);

  useEffect(() => {
    // Clear both stores when component mounts
    // useSelectedLadderOrdersStore.getState().clearAllOrders();
    // useBondLadderRungFlowStore.getState().clearLadderData();

    // Cleanup function when component unmounts
    return () => {
      // Clear both stores when leaving the page
      // useSelectedLadderOrdersStore.getState().clearAllOrders();
      // useBondLadderRungFlowStore.getState().clearLadderData();
    };
  }, []);

  const handleRungChange = (event, newValue) => {
    if (loadingTimerRef.current) {
      clearTimeout(loadingTimerRef.current);
    }
    const targetOrder = selectedOrders[newValue];
    const targetExecuted = !!(
      targetOrder &&
      (
        targetOrder.isEditable === false ||
        targetOrder.status === 'DONE' ||
        targetOrder.protocol === 'RFQ'
      )
    );

    previousRung.current = selectedRung;
    setIsRungSwitching(!targetExecuted);
    setSelectedRung(newValue);
    setPage(0);
  };

  useEffect(() => {
    // Stop rung switching loader immediately if current rung is executed
    if (isRungSwitching && isSelectedRungExecuted) {
      setIsRungSwitching(false);
      return;
    }
    if (isRungSwitching && !loading && previousRung.current !== selectedRung) {
      loadingTimerRef.current = setTimeout(() => {
        setIsRungSwitching(false);
      }, 500);
    }

    return () => {
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
      }
    };
  }, [loading, isRungSwitching, selectedRung, isSelectedRungExecuted]);

  const handleRowClick = (index) => {
    setSelectCusip(bondData[index].cusip);
    const updatedOrder = {
      cusip: bondData[index].cusip,
      side: "BUY",
      price: bondData[index].price,
      qty: bondData[index].qty
    };
    setActualOrderDataZus(updatedOrder);
    setOeOpen(true);
  };

  const handleCusipClick = (index, rowData) => {
    setSelectedBondDetails(rowData);
    setBondDetailsOpen(true);
  };

  // Function to check if a rung has orders selected
  const rungHasOrders = (rungIndex) => {
    return selectedOrders[rungIndex] !== undefined;
  };

  const handleNavigateToPreview = () => {
    navigate('/bond-ladder-preview', { 
      state: { 
        bondLadderId: ladderData?.bondLadderId, 
        ladderData 
      }
    });
  };

  // Always redirect to /order?view=0&tab=2 on browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      // Only redirect if currently on bond ladder details page
      if (window.location.pathname.startsWith('/bond-ladder-detail')) {
        window.location.replace('/order?view=0&tab=2');
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: 'calc(100vh - 84px)',
      width: '100%',
      minWidth: '600px',
    }}>
       <BondLadderHeader
        input={currentFilters}
        totalElements={totalElements}
        loading={uiLoading}
        lastUpdatedAt={lastUpdatedAt}
        selectedRung={selectedRung + 1}
        totalRungs={rungsDetail.length}
        onCustomizeClick={() => setCcOpen(true)}
        onDownloadCSV={handleDownloadCSV}
        csvLoading={csvLoading}
        customizeButtonRef={customizeButtonRef}
        onDeleteChip={handleDeleteChip}
        ladderData={ladderData}
        isRungExecuted={isSelectedRungExecuted} // <-- added
      />

      <Box
        ref={layoutRef}
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          flex: 1,
          overflow: 'hidden',
          width: '100%',
        }}
      >
        <Box sx={{
          display: 'flex',
          flex: 1,
          minHeight: 0,
          transition: 'all 0.3s ease',
          overflow: 'hidden',
          flexDirection: 'column',
          width: '100%',
        }}>
          <Box sx={{
            display: 'flex',
            flex: 1,
            overflow: 'hidden',
            width: '100%',
            // Ensure the top main area keeps a minimum height
            minHeight: `${MIN_MAIN_TOP_HEIGHT}px`,
          }}>
            <Box sx={{
              borderRight: 1,
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: 'rgba(38, 38, 38, 0.8)',
              height: '100%',
              minWidth: { xs: '80px', sm: '100px', md: '120px' },
              maxWidth: { xs: '110px', sm: '140px', md: '180px' },
              flexShrink: 0,
              overflow: 'hidden',
            }}>
              <VerticalTabs
                orientation="vertical"
                value={selectedRung}
                onChange={handleRungChange}
                sx={{
                  height: '100%',
                  '& .MuiTabs-flexContainer': {
                    height: 'auto',
                    minHeight: '100%',
                    flexDirection: 'column', // Ensure proper direction
                  },
                }}
              >
                {rungsDetail.map((_, index) => (
                  <VerticalTab
                    key={index}
                    label={
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <Typography sx={{ 
                          fontWeight: 'bold', 
                          fontSize: '14px', 
                          textTransform: 'none',
                          color: selectedRung === index ? tokens.colors.white : 'inherit' // white when selected
                        }}>
                          {`Rung${index + 1}`}
                        </Typography>
                        <Typography sx={{ 
                          fontSize: '10px', 
                          opacity: 0.8, 
                          mt: 0.1,
                          color: selectedRung === index ? tokens.colors.white : 'inherit' // white when selected
                        }}>
                          {ladderData?.amountPerRung ? `$${Number(ladderData.amountPerRung).toLocaleString()}` : '$0'}
                        </Typography>
                      </Box>
                    }
                    hasOrders={rungHasOrders(index)}
                    sx={{
                      
                      width: '100%',
                      typography: 'body1',
                      fontSize: { xs: '0.8rem', sm: '0.875rem', md: '0.95rem' },
                      pr: rungHasOrders(index) ? '30px' : '12px', // Extra padding when dot is shown
                      textTransform: 'none', // Ensure no text-transform is applied to the tab itself
                      // Last child doesn't need a bottom border
                      ...(index === rungsDetail.length - 1 ? { borderBottom: 'none' } : {})
                    }}
                  />
                ))}
              </VerticalTabs>
            </Box>

            <Box sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              position: 'relative',
              minWidth: 0,
            }}>
              <Fade in={uiLoading} timeout={{ enter: 150, exit: 300 }}>
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: withAlpha(tokens.colors.bodyBackgroundColor, 0.7),
                    zIndex: 10,
                    display: uiLoading ? "flex" : "none",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CircularProgress size={60} sx={{ color: tokens.palette.primary }} />
                  <Typography
                    variant="h6"
                    sx={{
                      color: tokens.colors.fontColour,
                      mt: 2,
                      fontWeight: "bold",
                    }}
                  >
                    Loading Rung {selectedRung + 1} Data...
                  </Typography>
                </Box>
              </Fade>

              <Fade in={!uiLoading} timeout={{ enter: 300, exit: 150 }}>
                <Box sx={{ flex: 1, overflow: 'hidden', minWidth: 0 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      height: "100%",
                      backgroundColor: tokens.colors.defaultGridRowColor,
                      borderRadius: "0px",
                      overflow: "hidden",
                      border: `1px solid ${tokens.colors.bodyBorderColor50}`,
                    }}
                  >
                    <BondTable
                      selectedColumns={selectedColumns}
                      filteredData={isSelectedRungExecuted ? [executedOrderForSelectedRung] : bondData}
                      handleRowClick={()=>{}}
                      handleCusipClick={handleCusipClick}
                      sectionKey='bondLadder'
                      showSelectionColumn={true}
                      currentRung={selectedRung}
                      disabled={uiLoading}
                      onSort={isSelectedRungExecuted ? () => {} : handleSortingDataFetching}
                      sortConfig={sortConfig}
                      isExecutedRung={isSelectedRungExecuted}
                    />
                  </Paper>
                </Box>
              </Fade>
            </Box>
          </Box>

          {/* Pagination row (measured for layout calculations) */}
          <Box
            ref={paginationRef}
            sx={{
              width: "100%",
              color: tokens.colors.fontColour,
              backgroundColor: tokens.colors.bodyBackgroundColor,
              borderTop: `1px solid ${tokens.colors.bodyBorderColor50}`,
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              px: { xs: 1, sm: 1, md: 2 },
              flexShrink: 0,
            }}
          >
            <TablePagination
              component="div"
              sx={{
                ".MuiSelect-select": { p: "4px 24px 5px 8px !important" },
                color: tokens.colors.fontColour,
                "& .MuiTablePagination-select": { color: tokens.colors.fontColour },
                "& .MuiTablePagination-selectIcon": { color: tokens.colors.fontColour85 },
                "& .MuiTablePagination-displayedRows": { color: tokens.colors.fontColour85 },
                "& .MuiIconButton-root": { color: tokens.colors.fontColour85 },
              }}
              showFirstButton
              showLastButton
              rowsPerPage={itemsPerPage}
              rowsPerPageOptions={[20, 50, 100]}
              page={page}
              onRowsPerPageChange={(event) => {
                setItemsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
              onPageChange={(_, newPage) => setPage(newPage)}
              count={isSelectedRungExecuted ? 1 : totalElements}
              labelRowsPerPage={window.innerWidth < 800 ? '' : 'Rows per page:'}
              // Custom displayedRows function to show actual filtered count
              labelDisplayedRows={({ from, to, count }) => {
                const visibleRows = isSelectedRungExecuted ? 1 : bondData.length;
                const actualTo = Math.min(to, (page * itemsPerPage) + visibleRows);
                return window.innerWidth < 500 
                  ? `${from.toLocaleString()}-${actualTo.toLocaleString()} / ${count.toLocaleString()}`
                  : `${from.toLocaleString()}-${actualTo.toLocaleString()} of ${count.toLocaleString()}`;
              }}
            />
          </Box>
        </Box>

        {/* Resizable preview area */}
        <Collapse in={hasSelectedOrders} timeout={300} unmountOnExit>
          <ResizableContainer
            minHeight={previewMinHeight}
            maxHeight={allowedMaxPreviewHeight || (PREVIEW_HEADER_BAR_HEIGHT + MIN_TABLE_HEIGHT)}
            initialHeight={Math.min(previewHeight, allowedMaxPreviewHeight || previewHeight)}
            onHeightChange={setPreviewHeight}
            onResizingChange={setIsPreviewResizing} // NEW
          >
            {/* Preview Header (inside resizable area, the handle is above this bar) */}
            <Box sx={{ 
              display: "flex", 
              alignItems: "center",
              justifyContent: "space-between",
              p: { xs: 0.5, sm: 0.75, md: 0.75 },
              backgroundColor: tokens.colors.defaultGridRowColor,
              zIndex: 10,
              height: `${PREVIEW_HEADER_BAR_HEIGHT}px`,
              flexShrink: 0,
              borderTop: `1px solid ${tokens.colors.bodyBorderColor50}`,
            }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: tokens.colors.fontColour,
                  fontWeight: "bold",
                  fontSize: { xs: '0.9rem', sm: '1rem', md: '1.25rem' },
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                Selected Orders Preview ({Object.keys(selectedOrders).length.toLocaleString()})
              </Typography>
              
              <Button
                variant="contained"
                color="primary"
                disabled={Object.keys(selectedOrders).length === 0}
                onClick={handleNavigateToPreview}
                sx={{ 
                  borderRadius: "8px",
                  fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem' },
                  px: { xs: 2, sm: 3, md: 4 },
                  py: 0.75,
                  minWidth: { xs: '100px', sm: '120px' },
                }}
              >
                Preview {Object.keys(selectedOrders).length > 0 ? Object.keys(selectedOrders).length.toLocaleString() : ""} Order{Object.keys(selectedOrders).length !== 1 ? 's' : ''}
              </Button>
            </Box>

            {/* Table area gets remaining space; we cap its height to (container - header) */}
            <Box sx={{ height: `calc(100% - ${PREVIEW_HEADER_BAR_HEIGHT}px)` }}>
              <SelectedOrdersPreviewTable 
                selectedColumns={selectedColumns} 
                selectedRung={selectedRung}
                bondLadderId={ladderData?.bondLadderId}
                ladderData={ladderData}
                removeOrder={removeOrder}
                // Fit parent's available height so it follows DOM changes instantly
                fitParentHeight
                disableHeightTransition={isPreviewResizing}
                // Keep maxTableHeight for non-drag recalcs; ignored when fitParentHeight is true
                maxTableHeight={Math.max(0, (previewHeight || 0) - PREVIEW_HEADER_BAR_HEIGHT)}
              />
            </Box>
          </ResizableContainer>
        </Collapse>
      </Box>

      <OrderEntry
        open={oeOpen}
        onClose={() => setOeOpen(false)}
        source="buyOrderEntry"
        initialData={{ account: "GB3:GOV", bondType: "Treasury", quantity: "100000", cusip: selectCusip }}
        token={token}
        readOnly={readOnly}
      />

      <CustomizeColumns
        open={ccOpen}
        onClose={() => setCcOpen(false)}
        allColumns={allColumns}
        selectedColumns={selectedColumns}
        setSelectedColumnsInventory={setSelectedColumns}
        anchorEl={customizeButtonRef.current}
        sectionKey="bondLadder"
      />

      <BondDetailsModal
        open={bondDetailsOpen}
        onClose={() => setBondDetailsOpen(false)}
        bondDetails={selectedBondDetails}
      />
    </Box>
  );
};

export default BondLadderDetails;
